import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import communicationService from '../../services/communicationService';
import studentsService from '../../services/studentsService';
import staffService from '../../services/staffService';
import classesService from '../../services/classesService';
import './TeacherSelector.css';

/**
 * TeacherParentSelector Component
 * 
 * Two-step selection process:
 * 1. Teacher selects one of their classes
 * 2. System fetches parents/students from that class
 * 3. Teacher selects parent to message
 * 
 * @param {string} selectedClass - Currently selected class ID
 * @param {function} onClassChange - Callback when class selection changes
 * @param {function} onParentSelected - Callback when parent is selected
 * @param {function} onCancel - Callback when user cancels selection
 * @param {array} recentlyMessaged - List of recently messaged parent IDs
 */
const TeacherParentSelector = ({
  selectedClass,
  onClassChange,
  onParentSelected,
  onCancel,
  recentlyMessaged = []
}) => {
  const { user } = useAuth();
  
  // States
  const [classes, setClasses] = useState([]);
  const [parents, setParents] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [error, setError] = useState(null);
  
  // Search and sort
  const [searchParent, setSearchParent] = useState('');
  const [sortBy, setSortBy] = useState('name');

  /**
   * Fetch teacher's classes
   */
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoadingClasses(true);
        setError(null);
        
        const data = await staffService.getMyClasses();
        const items = data.items || [];
        setClasses(items);
        
        // Auto-select first class
        if (items.length > 0 && !selectedClass) {
          const firstClass = items[0];
          const classId = firstClass.class_id || firstClass.id;
          onClassChange(classId);
        }
      } catch (err) {
        setError(`Failed to load classes: ${err.message}`);
        console.error('Error fetching classes:', err);
      } finally {
        setLoadingClasses(false);
      }
    };
    
    fetchClasses();
  }, [onClassChange, selectedClass]);

  /**
   * Fetch parents/students from selected class
   */
  useEffect(() => {
    const fetchParents = async () => {
      if (!selectedClass) return;
      
      try {
        setLoadingParents(true);
        setError(null);
        setParents([]);
        
        // Step 1: Fetch all students in the selected class
        const studentsData = await classesService.getClassStudents(selectedClass);
        const classStudents = studentsData.items || [];
        
        if (classStudents.length === 0) {
          setParents([]);
          setLoadingParents(false);
          return;
        }
        
        // Step 2: Extract parent info from students
        // For each student, fetch their parents using the student endpoint
        const parentMap = new Map();
        
        for (const student of classStudents) {
          try {
            // Fetch parents for this student (teacher-accessible endpoint)
            const studentId = student.id || student.id;
            const parentData = await studentsService.getStudentParents(studentId);
            const parents = Array.isArray(parentData) ? parentData : parentData.items || [];
            
            // Add each parent to the map
            parents.forEach(parent => {
              const parentId = parent.id || parent.parent_id;
              
              if (!parentMap.has(parentId)) {
                parentMap.set(parentId, {
                  parent_id: parentId,
                  user_id: parent.user_id || null,  // Store user_id for messaging
                  first_name: parent.first_name || 'Unknown',
                  last_name: parent.last_name || 'Parent',
                  email: parent.email || '',
                  phone: parent.phone || '',
                  relationship: parent.relationship || '',
                  ward_names: new Set(),
                  student_ids: new Set()
                });
              }
              
              // Add this student to parent's wards
              const studentName = `${student.first_name || 'Unknown'} ${student.last_name || 'Student'}`;
              const parentEntry = parentMap.get(parentId);
              parentEntry.ward_names.add(studentName);
              parentEntry.student_ids.add(studentId);
            });
          } catch (err) {
            console.error(`Error fetching parents for student ${student.student_id}:`, err);
            // Continue with next student if this one fails
          }
        }
        
        // Step 3: Also fetch conversations to get parent email/contact if not in student data
        try {
          const conversationData = await communicationService.getConversations();
          const conversations = conversationData.items || [];
          
          conversations.forEach(conv => {
            const parentId = conv.other_user_id || conv.parent_id || conv.user_id;
            const studentId = conv.student_id || conv.message_student_id;
            
            // Only include if student is in this class
            const isInClass = classStudents.some(s => (s.student_id || s.id) === studentId);
            if (!isInClass) return;
            
            // Add or update parent with conversation data
            if (!parentMap.has(parentId)) {
              const firstName = conv.first_name || conv.other_user_name?.split(' ')[0] || 'Unknown';
              const lastName = conv.last_name || conv.other_user_name?.split(' ').slice(1).join(' ') || 'Parent';
              
              parentMap.set(parentId, {
                parent_id: parentId,
                user_id: conv.other_user_id || null,  // Store user_id for messaging
                conversation_id: conv.conversation_id,
                first_name: firstName,
                last_name: lastName,
                email: conv.email || '',
                phone: conv.phone || '',
                ward_names: new Set(),
                student_ids: new Set()
              });
            } else if (!parentMap.get(parentId).conversation_id) {
              // Store conversation_id if not already present
              parentMap.get(parentId).conversation_id = conv.conversation_id;
              // Also update user_id if not set
              if (!parentMap.get(parentId).user_id && conv.other_user_id) {
                parentMap.get(parentId).user_id = conv.other_user_id;
              }
            }
            
            // Update ward info from conversation
            const parentEntry = parentMap.get(parentId);
            const studentInClass = classStudents.find(s => (s.student_id || s.id) === studentId);
            if (studentInClass) {
              const studentName = `${studentInClass.first_name || 'Unknown'} ${studentInClass.last_name || 'Student'}`;
              parentEntry.ward_names.add(studentName);
              parentEntry.student_ids.add(studentId);
            }
          });
        } catch (err) {
          console.error('Error fetching conversations:', err);
        }
        
        // Convert Sets to Arrays
        const normalizedParents = Array.from(parentMap.values()).map(parent => ({
          ...parent,
          ward_names: Array.from(parent.ward_names),
          student_ids: Array.from(parent.student_ids)
        }));
        
        setParents(normalizedParents);
        setSelectedParent(null);
      } catch (err) {
        if (err.message.includes('404')) {
          setParents([]);
        } else {
          setError(`Failed to load parents: ${err.message}`);
        }
        console.error('Error fetching parents:', err);
      } finally {
        setLoadingParents(false);
      }
    };
    
    fetchParents();
  }, [selectedClass]);

  /**
   * Filter and sort parents
   */
  const filteredParents = useMemo(() => {
    let filtered = parents.filter(parent => {
      const fullName = `${parent.first_name || 'Unknown'} ${parent.last_name || 'Parent'}`;
      const searchLower = searchParent.toLowerCase();
      
      return fullName.toLowerCase().includes(searchLower) || 
             (parent.email || '').toLowerCase().includes(searchLower);
    });

    // Sort methods
    if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const nameA = `${a.first_name || 'Unknown'} ${a.last_name || 'Parent'}`;
        const nameB = `${b.first_name || 'Unknown'} ${b.last_name || 'Parent'}`;
        return nameA.localeCompare(nameB);
      });
    } else if (sortBy === 'recent') {
      filtered.sort((a, b) => {
        const aRecent = recentlyMessaged.includes(a.parent_id) || recentlyMessaged.includes(a.conversation_id) ? 0 : 1;
        const bRecent = recentlyMessaged.includes(b.parent_id) || recentlyMessaged.includes(b.conversation_id) ? 0 : 1;
        return aRecent - bRecent;
      });
    }

    return filtered;
  }, [parents, searchParent, sortBy, recentlyMessaged]);

  /**
   * Handle parent confirmation
   */
  const handleConfirm = useCallback(() => {
    if (!selectedParent) {
      setError('Please select a parent');
      return;
    }
    
    const parentName = `${selectedParent.first_name || 'Unknown'} ${selectedParent.last_name || 'Parent'}`;
    // Use user_id for messaging (most reliable), fallback to conversation_id or parent_id
    const receiverId = selectedParent.user_id || selectedParent.conversation_id || selectedParent.parent_id;
    onParentSelected(receiverId, parentName);
  }, [selectedParent, onParentSelected]);

  return (
    <div className="selector-overlay">
      <div className="selector-modal">
        {/* Header */}
        <div className="selector-header">
          <h2>Select a Parent</h2>
          <button className="selector-close" onClick={onCancel} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="selector-content">
          {/* Step 1: Select Class */}
          <div className="selector-section">
            <label className="selector-label">Your Class</label>
            {loadingClasses ? (
              <div className="selector-loading">
                <div className="chat-spinner"></div>
                <p>Loading classes...</p>
              </div>
          ) : classes.length === 0 ? (
            <div className="selector-empty">No classes found</div>
          ) : (
            <select 
              className="selector-input"
              value={selectedClass || ''}
              onChange={(e) => onClassChange(e.target.value)}
            >
              <option value="">Select a class</option>
              {classes.map(cls => (
                <option key={cls.class_id || cls.id} value={cls.class_id || cls.id}>
                  {cls.class_name || cls.name} 
                  {cls.section ? ` (${cls.section})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Step 2: Select Parent */}
        {selectedClass && (
          <>
            {/* Error */}
            {error && (
              <div className="selector-error">
                <span>{error}</span>
                <button 
                  className="error-close"
                  onClick={() => setError(null)}
                  aria-label="Close error"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Search and Sort */}
            <div className="selector-section">
              <label className="selector-label">Parents</label>
              <div className="selector-controls">
                <input
                  type="text"
                  className="selector-search"
                  placeholder="Search parents..."
                  value={searchParent}
                  onChange={(e) => setSearchParent(e.target.value)}
                />
                <select 
                  className="selector-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="name">Sort: Name</option>
                  <option value="recent">Sort: Recent</option>
                </select>
              </div>
            </div>

            {/* Parent List */}
            <div className="selector-list">
              {loadingParents ? (
                <div className="selector-loading">
                  <div className="chat-spinner"></div>
                  <p>Loading parents...</p>
                </div>
              ) : filteredParents.length === 0 ? (
                <div className="selector-empty">
                  {parents.length === 0 
                    ? 'No parents found for this class'
                    : 'No matching parents'}
                </div>
              ) : (
                filteredParents.map(parent => (
                  <div
                    key={parent.parent_id || parent.conversation_id}
                    className={`selector-item ${selectedParent?.parent_id === parent.parent_id || selectedParent?.conversation_id === parent.conversation_id ? 'selected' : ''}`}
                    onClick={() => setSelectedParent(parent)}
                  >
                    {/* Avatar */}
                    <div className="selector-avatar">
                      {(parent.first_name || 'U').charAt(0)}
                      {(parent.last_name || 'P').charAt(0)}
                    </div>
                    
                    {/* Info */}
                    <div className="selector-info">
                      <div className="selector-name">
                        {parent.first_name || 'Unknown'} {parent.last_name || 'Parent'}
                      </div>
                      {parent.ward_names && parent.ward_names.length > 0 && (
                        <div className="selector-subject">
                          Ward{parent.ward_names.length > 1 ? 's' : ''}: {parent.ward_names.join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Selection indicator */}
                    {(selectedParent?.parent_id === parent.parent_id || selectedParent?.conversation_id === parent.conversation_id) && (
                      <div className="selector-checkmark">✓</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
        </div>

        {/* Footer */}
        {selectedClass && (
          <div className="selector-footer">
            <button 
              className="selector-btn-cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button 
              className={`selector-btn-confirm ${selectedParent ? 'active' : 'disabled'}`}
              onClick={handleConfirm}
              disabled={!selectedParent}
            >
              Message {selectedParent ? `${selectedParent.first_name || 'Unknown'} ${selectedParent.last_name || 'Parent'}` : 'Parent'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherParentSelector;
